/**
 * TREATX-ADMIN-MAINTENANCE-01 — Super Admin → Maintenance
 * -------------------------------------------------------
 * Execution surface only. One named action (AC01 legal-corpus cleanup) that
 * calls the canonical backend implementation; no cleanup logic lives here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, Loader2, ShieldCheck, Wrench,
} from 'lucide-react';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

const STATUS_LABEL = {
  NOT_RUN: 'Not Run',
  COMPLETED: 'Completed',
  ALREADY_COMPLETED: 'Completed',
  COMPLETED_WITH_SKIPS: 'Completed with Skips',
  BLOCKED: 'Blocked',
  FAILED: 'Failed',
};

const STATUS_CLS = {
  NOT_RUN: 'bg-slate-100 text-slate-600 border-slate-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  ALREADY_COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  COMPLETED_WITH_SKIPS: 'bg-amber-50 text-amber-800 border-amber-300',
  BLOCKED: 'bg-amber-50 text-amber-800 border-amber-300',
  FAILED: 'bg-rose-50 text-rose-700 border-rose-300',
};

const Row = ({ label, value, testid, tone }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span
      className={`text-xs font-medium text-right ${tone === 'bad' ? 'text-rose-700' : 'text-slate-900'}`}
      data-testid={testid}
    >
      {value}
    </span>
  </div>
);

const CookiePolicyRemovalCard = ({ action, onDone }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setRunning(true);
    setConfirmOpen(false);
    try {
      const { data } = await api.post('/admin/maintenance/remove-obsolete-cookie-policy');
      setResult(data);
      setError(null);
      if (data?.status === 'BLOCKED') toast.warning('Blocked — nothing was deleted');
      else if (data?.status === 'FAILED') toast.error('Removal reported a failure');
      else if (data?.status === 'ALREADY_COMPLETED') toast.success('Already completed — nothing to do');
      else toast.success('Obsolete Cookie Policy removed — key is free');
      if (onDone) await onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.message || d || e?.message || 'Execution failed');
      toast.error(d?.message || d || 'Execution failed');
    } finally {
      setRunning(false);
    }
  };

  const status = result?.status || action?.status || 'NOT_RUN';
  const post = result?.post_state;

  return (
    <Card className="border-slate-200 mt-6" data-testid="cookie-policy-removal-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Remove Obsolete Cookie Policy
            </h2>
            <p className="text-sm text-slate-600 mt-2 max-w-xl">
              Removes the obsolete SoftLaunch <code>cookie_policy</code> record and its known
              obsolete Public Legal Footer binding and re-consent rule, after verifying that
              no unexpected dependency or user evidence exists.
            </p>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${STATUS_CLS[status]}`}
            data-testid="cookie-removal-status"
          >
            {STATUS_LABEL[status] || status}
          </span>
        </div>

        <div
          className="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
          data-testid="cookie-removal-temporary-note"
        >
          <div className="font-semibold flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> TEMPORARY MAINTENANCE ACTION
          </div>
          <div className="mt-1">
            This action exists only to free the canonical <code>cookie_policy</code> key before
            publishing the approved Cookie Policy through Agreement Management. Remove this
            action after Owner verification.
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={running}
            data-testid="remove-obsolete-cookie-policy-btn"
          >
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Remove Obsolete Cookie Policy
          </Button>
          {action?.last_execution && (
            <span className="text-xs text-slate-500" data-testid="cookie-removal-last-execution">
              Last run {action.last_execution.executed_at} ·
              {' '}{action.last_execution.before_count} → {action.last_execution.after_count}
            </span>
          )}
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700"
            data-testid="cookie-removal-error">
            {String(error)}
          </div>
        )}

        {result && (
          <div className="mt-6 border-t pt-5" data-testid="cookie-removal-result">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-900">Result</span>
            </div>
            <Row label="Before platform_content count"
              value={result.platform_content_before ?? '—'} testid="cookie-before-count" />
            <Row label="After platform_content count"
              value={result.platform_content_after ?? '—'} testid="cookie-after-count" />
            <Row label="cookie_policy record" value={post?.cookie_policy_record || '—'}
              tone={post?.cookie_policy_record === 'ABSENT' ? 'ok' : 'bad'}
              testid="cookie-record-state" />
            <Row label="Cookie Policy key" value={post?.cookie_policy_key || '—'}
              tone={post?.cookie_policy_key === 'FREE' ? 'ok' : 'bad'}
              testid="cookie-key-state" />
            <Row label="cookie_policy version rows"
              value={post?.cookie_policy_version_rows ?? '—'}
              tone={post?.cookie_policy_version_rows === 0 ? 'ok' : 'bad'}
              testid="cookie-version-rows" />
            <Row label="cookie_policy re-consent rule"
              value={post?.cookie_policy_reconsent_rule || '—'}
              tone={post?.cookie_policy_reconsent_rule === 'ABSENT' ? 'ok' : 'bad'}
              testid="cookie-reconsent-state" />
            <Row label="Known obsolete footer binding"
              value={result.performed?.footer_binding === 'DETACHED_KEY'
                ? 'KEY DETACHED (rule still serves other documents)'
                : (post?.obsolete_footer_binding || '—')}
              testid="cookie-footer-binding-state" />
            <Row label="Active rules still referencing the key"
              value={(post?.obsolete_footer_binding_references_key || []).length === 0
                ? 'NONE' : (post.obsolete_footer_binding_references_key).join(', ')}
              tone={(post?.obsolete_footer_binding_references_key || []).length === 0 ? 'ok' : 'bad'}
              testid="cookie-active-refs" />
            <Row label="Protected records" value={result.protected_records || '—'}
              tone={result.protected_records === 'UNCHANGED' ? 'ok' : 'bad'}
              testid="cookie-protected" />
            <Row label="Bootstrap sentinels" value={result.bootstrap_sentinels || '—'}
              tone={result.bootstrap_sentinels === 'INTACT' ? 'ok' : 'bad'}
              testid="cookie-sentinels" />
            <Row label="Unexpected dangling active bindings"
              value={(post?.dangling_active_bindings || []).length === 0
                ? 'NONE'
                : (post.dangling_active_bindings)
                  .map((d) => `${d.binding_id} → ${d.missing_key}`).join(', ')}
              tone={(post?.dangling_active_bindings || []).length === 0 ? 'ok' : 'bad'}
              testid="cookie-dangling" />
            <Row label="Restart/re-seed protection" value={result.reseed_protection || '—'}
              tone={result.reseed_protection === 'PASS' ? 'ok' : 'bad'}
              testid="cookie-reseed" />
            <Row label="Atomic companion cleanup" value={result.transaction || '—'}
              testid="cookie-transaction" />

            {(result.blockers || []).length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-amber-800 mb-1">
                  Blocked — nothing was deleted (REASON)
                </div>
                <ul className="space-y-1" data-testid="cookie-blockers-list">
                  {(result.blockers || []).map((b) => (
                    <li key={b.code} className="text-xs text-slate-700">
                      <b>{b.code}</b> | {b.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="cookie-removal-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Remove Obsolete Cookie Policy?</DialogTitle>
            <DialogDescription className="text-left">
              This operation permanently removes the obsolete <code>cookie_policy</code> record,
              its versions, its known obsolete Public Legal Footer binding, and its re-consent rule.
              The operation will abort without deletion if unexpected dependencies or evidence
              are found.
              <br /><br />
              Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}
              data-testid="cookie-removal-confirm-cancel">
              Cancel
            </Button>
            <Button onClick={run} disabled={running}
              data-testid="cookie-removal-confirm-run">
              Remove Cookie Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const StaleTermsRemovalCard = ({ action, onDone }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setRunning(true);
    setConfirmOpen(false);
    try {
      const { data } = await api.post('/admin/maintenance/remove-stale-terms-of-service');
      setResult(data);
      setError(null);
      if (data?.status === 'BLOCKED') toast.warning('Blocked — nothing was deleted');
      else if (data?.status === 'FAILED') toast.error('Removal reported a failure');
      else if (data?.status === 'ALREADY_COMPLETED') toast.success('Already completed — nothing to do');
      else toast.success('Stale Terms of Service removed — key is free');
      if (onDone) await onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.message || d || e?.message || 'Execution failed');
      toast.error(d?.message || d || 'Execution failed');
    } finally {
      setRunning(false);
    }
  };

  const status = result?.status || action?.status || 'NOT_RUN';
  const post = result?.post_state;
  const checks = result?.checks;

  return (
    <Card className="border-slate-200 mt-6" data-testid="stale-terms-removal-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Remove Stale Terms of Service
            </h2>
            <p className="text-sm text-slate-600 mt-2 max-w-xl">
              Frees the canonical <code>terms_of_service</code> key by removing the stale
              <strong> archived </strong> record, its version rows and its orphan re-consent
              rule. Runs only if that record has no acceptance evidence and no rule, pack,
              policy-set or consent-module reference — otherwise it aborts without deleting
              anything.
            </p>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${STATUS_CLS[status]}`}
            data-testid="stale-terms-removal-status"
          >
            {STATUS_LABEL[status] || status}
          </span>
        </div>

        <div
          className="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
          data-testid="stale-terms-removal-temporary-note"
        >
          <div className="font-semibold flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> TEMPORARY MAINTENANCE ACTION
          </div>
          <div className="mt-1">
            This action exists only to free the canonical <code>terms_of_service</code> key
            before the Owner publishes the new canonical Terms of Service through Agreement
            Management. It never creates a document. Remove this action after Owner
            verification.
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={running}
            data-testid="remove-stale-terms-of-service-btn"
          >
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Remove Stale Terms of Service
          </Button>
          {action?.last_execution && (
            <span className="text-xs text-slate-500" data-testid="stale-terms-last-execution">
              Last run {action.last_execution.executed_at} ·
              {' '}{action.last_execution.before_count} → {action.last_execution.after_count}
            </span>
          )}
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700"
            data-testid="stale-terms-removal-error">
            {String(error)}
          </div>
        )}

        {result && (
          <div className="mt-6 border-t pt-5" data-testid="stale-terms-removal-result">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-900">Result</span>
            </div>
            <Row label="Parent status (must be archived)"
              value={checks?.A_parent_status ?? (post?.terms_of_service_record === 'ABSENT' ? 'removed' : '—')}
              tone={!checks || checks.A_parent_status === 'archived' ? 'ok' : 'bad'}
              testid="stale-terms-parent-status" />
            <Row label="Acceptance evidence rows"
              value={checks?.B_evidence_rows ?? post?.evidence_rows ?? '—'}
              tone={(checks?.B_evidence_rows ?? post?.evidence_rows) === 0 ? 'ok' : 'bad'}
              testid="stale-terms-evidence" />
            <Row label="Active rules referencing the key"
              value={(checks?.C_active_bindings || []).length === 0 ? 'NONE' : checks.C_active_bindings.join(', ')}
              tone={(checks?.C_active_bindings || []).length === 0 ? 'ok' : 'bad'}
              testid="stale-terms-active-bindings" />
            <Row label="Inactive rules referencing the key"
              value={(checks?.D_inactive_bindings || []).length === 0 ? 'NONE' : checks.D_inactive_bindings.join(', ')}
              tone={(checks?.D_inactive_bindings || []).length === 0 ? 'ok' : 'bad'}
              testid="stale-terms-inactive-bindings" />
            <Row label="Pack / policy-set / consent-module references"
              value={[...(checks?.E_pack_references || []), ...(checks?.F_policy_set_references || []),
                ...(checks?.G_consent_module_references || [])].join(', ') || 'NONE'}
              testid="stale-terms-other-refs" />
            <Row label="Orphan re-consent rule found"
              value={checks?.H_reconsent_rows ?? '—'}
              testid="stale-terms-reconsent-found" />
            <Row label="terms_of_service record" value={post?.terms_of_service_record || '—'}
              tone={post?.terms_of_service_record === 'ABSENT' ? 'ok' : 'bad'}
              testid="stale-terms-record-state" />
            <Row label="Terms of Service key" value={post?.terms_of_service_key || '—'}
              tone={post?.terms_of_service_key === 'FREE' ? 'ok' : 'bad'}
              testid="stale-terms-key-state" />
            <Row label="terms_of_service version rows"
              value={post?.terms_of_service_version_rows ?? '—'}
              tone={post?.terms_of_service_version_rows === 0 ? 'ok' : 'bad'}
              testid="stale-terms-version-rows" />
            <Row label="terms_of_service re-consent rule"
              value={post?.terms_of_service_reconsent_rule || '—'}
              tone={post?.terms_of_service_reconsent_rule === 'ABSENT' ? 'ok' : 'bad'}
              testid="stale-terms-reconsent-state" />
            <Row label="Rules/packs/sets left pointing at the removed key"
              value={[...(post?.dangling_bindings || []).map((d) => `${d.binding_id} → ${d.missing_key}`),
                ...(post?.dangling_pack_references || []),
                ...(post?.dangling_policy_set_references || []),
                ...(post?.dangling_consent_module_references || [])].join(', ') || 'NONE'}
              tone={(post?.dangling_bindings || []).length === 0 ? 'ok' : 'bad'}
              testid="stale-terms-dangling" />
            <Row label="Protected records" value={result.protected_records || '—'}
              tone={result.protected_records === 'UNCHANGED' ? 'ok' : 'bad'}
              testid="stale-terms-protected" />
            <Row label="Bootstrap sentinels" value={result.bootstrap_sentinels || '—'}
              tone={result.bootstrap_sentinels === 'INTACT' ? 'ok' : 'bad'}
              testid="stale-terms-sentinels" />
            <Row label="Restart/re-seed protection" value={result.reseed_protection || '—'}
              tone={result.reseed_protection === 'PASS' ? 'ok' : 'bad'}
              testid="stale-terms-reseed" />
            <Row label="Atomic removal" value={result.transaction || '—'}
              testid="stale-terms-transaction" />

            {(result.blockers || []).length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-amber-800 mb-1">
                  Blocked — nothing was deleted (REASON)
                </div>
                <ul className="space-y-1" data-testid="stale-terms-blockers-list">
                  {(result.blockers || []).map((b) => (
                    <li key={b.code} className="text-xs text-slate-700">
                      <b>{b.code}</b> | {b.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="stale-terms-removal-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Remove Stale Terms of Service?</DialogTitle>
            <DialogDescription className="text-left">
              This operation permanently removes the stale <strong>archived</strong>
              {' '}<code>terms_of_service</code> record, its version rows and its orphan
              re-consent rule — nothing else. It aborts without any deletion if the record
              is not archived, or if any acceptance evidence, rule, pack, policy-set or
              consent-module reference exists. It does NOT create the new Terms of Service.
              <br /><br />
              Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}
              data-testid="stale-terms-removal-confirm-cancel">
              Cancel
            </Button>
            <Button onClick={run} disabled={running}
              data-testid="stale-terms-removal-confirm-run">
              Remove Stale Terms of Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default function MaintenancePage() {
  const navigate = useNavigate();
  const { admin } = useTreatXAdminAuth();
  const isSuperAdmin = admin?.role === 'treatx_super_admin';

  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [cookieAction, setCookieAction] = useState(null);
  const [tosAction, setTosAction] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (admin && !isSuperAdmin) navigate('/treatx-admin/dashboard', { replace: true });
  }, [admin, isSuperAdmin, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/maintenance/actions');
      const list = data?.actions || [];
      setAction(list.find((a) => a.action_id === 'LEGAL_CORPUS_CLEANUP_AC01') || null);
      setCookieAction(list.find((a) => a.action_id === 'REMOVE_OBSOLETE_COOKIE_POLICY') || null);
      setTosAction(list.find((a) => a.action_id === 'REMOVE_STALE_TERMS_OF_SERVICE') || null);
      setError(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.message || d || e?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin, load]);

  const runCleanup = async () => {
    setRunning(true);
    setConfirmOpen(false);
    try {
      const { data } = await api.post('/admin/maintenance/legal-corpus-cleanup-ac01');
      setResult(data);
      setError(null);
      if (data?.status === 'FAILED') toast.error('Cleanup finished with failures');
      else if (data?.status === 'COMPLETED_WITH_SKIPS') toast.warning('Cleanup completed with skips');
      else toast.success('Legal corpus cleanup completed');
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.message || d || e?.message || 'Execution failed');
      toast.error(d?.message || d || 'Execution failed');
    } finally {
      setRunning(false);
    }
  };

  if (!isSuperAdmin) return null;

  const status = result?.status || action?.status || 'NOT_RUN';

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="admin-maintenance-page">
      <div className="max-w-3xl mx-auto px-4">
        <button
          onClick={() => navigate('/treatx-admin/dashboard')}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"
          data-testid="maintenance-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Wrench className="h-5 w-5 text-slate-500" /> Maintenance
        </h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">
          Controlled administrative operations. Super Admin only.
        </p>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Legal Corpus Cleanup — AC01
                </h2>
                <p className="text-sm text-slate-600 mt-2 max-w-xl">
                  Removes the Owner-approved obsolete/test legal-content records from the
                  deployed SoftLaunch database. Protected canonical Agreements, active
                  Category-B CMS content, Terms of Service and the three provider agreement
                  records are not modified. The existing AC01 dependency safety gate is
                  enforced before deletion.
                </p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${STATUS_CLS[status]}`}
                data-testid="maintenance-ac01-status"
              >
                {STATUS_LABEL[status] || status}
              </span>
            </div>

            <div
              className="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
              data-testid="maintenance-temporary-note"
            >
              <div className="font-semibold flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> TEMPORARY MAINTENANCE ACTION
              </div>
              <div className="mt-1">
                This action exists only to complete the SoftLaunch AC01 cleanup.
                Remove this action after Owner verification. The Maintenance page itself
                may remain for future controlled administrative operations.
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={loading || running}
                data-testid="run-legal-corpus-cleanup-btn"
              >
                {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Run Legal Corpus Cleanup
              </Button>
              {action?.last_execution && (
                <span className="text-xs text-slate-500" data-testid="maintenance-last-execution">
                  Last run {action.last_execution.executed_at} ·
                  {' '}{action.last_execution.removed_count} removed ·
                  {' '}{action.last_execution.skipped_count} skipped
                </span>
              )}
            </div>

            {error && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700"
                data-testid="maintenance-error">
                {String(error)}
              </div>
            )}

            {result && (
              <div className="mt-6 border-t pt-5" data-testid="maintenance-result">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-900">Result</span>
                </div>
                <Row label="Before platform_content count"
                  value={result.platform_content_before ?? '—'}
                  testid="result-before-count" />
                <Row label="After platform_content count"
                  value={result.platform_content_after ?? '—'}
                  testid="result-after-count" />
                <Row label="Targets removed"
                  value={(result.targets_removed || []).length}
                  testid="result-removed-count" />
                <Row label="Targets already absent"
                  value={(result.targets_already_absent || []).length}
                  testid="result-already-absent-count" />
                <Row label="Targets skipped"
                  value={(result.targets_skipped || []).length}
                  testid="result-skipped-count" />
                <Row label="Protected records" value={result.protected_records}
                  tone={result.protected_records === 'UNCHANGED' ? 'ok' : 'bad'}
                  testid="result-protected" />
                <Row label="Bootstrap sentinels" value={result.bootstrap_sentinels}
                  tone={result.bootstrap_sentinels === 'INTACT' ? 'ok' : 'bad'}
                  testid="result-sentinels" />
                <Row label="Dangling active bindings"
                  value={(result.dangling_active_bindings || []).length === 0
                    ? 'NONE'
                    : (result.dangling_active_bindings || [])
                      .map((d) => `${d.binding_id} → ${d.missing_key}`).join(', ')}
                  tone={(result.dangling_active_bindings || []).length === 0 ? 'ok' : 'bad'}
                  testid="result-dangling" />
                <Row label="Restart/re-seed protection" value={result.reseed_protection}
                  tone={result.reseed_protection === 'PASS' ? 'ok' : 'bad'}
                  testid="result-reseed" />
                <Row label="Cookie Policy key" value={result.cookie_policy_key}
                  tone={result.cookie_policy_key === 'FREE' ? 'ok' : 'bad'}
                  testid="result-cookie-policy-key" />

                {(result.targets_removed || []).length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold text-slate-700 mb-1">Removed keys</div>
                    <div className="text-xs text-slate-600 break-words" data-testid="result-removed-keys">
                      {(result.targets_removed || []).join(', ')}
                    </div>
                  </div>
                )}

                {(result.targets_skipped || []).length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold text-rose-700 mb-1">
                      Skipped targets (KEY | SKIP REASON)
                    </div>
                    <ul className="space-y-1" data-testid="result-skipped-list">
                      {(result.targets_skipped || []).map((s) => (
                        <li key={s.key} className="text-xs text-slate-700">
                          <b>{s.key}</b> | {s.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <CookiePolicyRemovalCard action={cookieAction} onDone={load} />
        <StaleTermsRemovalCard action={tosAction} onDone={load} />
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="maintenance-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Run Legal Corpus Cleanup?</DialogTitle>
            <DialogDescription className="text-left">
              This operation permanently removes obsolete SoftLaunch test/legacy records.
              Before deletion, the existing AC01 safety checks will verify dependencies.
              Any record that is no longer safe to remove must be SKIPPED rather than
              force-deleted. Protected records must remain unchanged.
              <br /><br />
              Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}
              data-testid="maintenance-confirm-cancel">
              Cancel
            </Button>
            <Button onClick={runCleanup} disabled={running}
              data-testid="maintenance-confirm-run">
              Run Cleanup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
